import { api } from "./api";
import { getAll as get } from "./pagination";

export interface EventCategory {
  id: number;
  name: string;
  description?: string | null;
  color: string;
  is_active: boolean;
  event_count: number;
}

export type EventStatus = "draft" | "published" | "cancelled" | "completed" | "postponed";

export interface SchoolEvent {
  id: number;
  name: string;
  description?: string | null;
  event_type: string;
  category: number;
  category_name: string;
  category_color: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  venue?: string | null;
  address?: string | null;
  organizer: number;
  organizer_name: string;
  contact_person?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  expected_attendees?: number | null;
  max_attendees?: number | null;
  registration_required: boolean;
  registration_deadline?: string | null;
  status: EventStatus;
  status_reason: string;
  status_changed_by_name: string;
  status_changed_at?: string | null;
  is_public: boolean;
  is_featured: boolean;
  /** Shown on the public sign-in page while published and not yet over. */
  show_on_sign_in: boolean;
  agenda?: string | null;
  requirements?: string | null;
  notes?: string | null;
  registered_count: number;
  attended_count: number;
  feedback_count: number;
  average_rating: number | null;
  /** The signed-in person's own registration, unless they withdrew. */
  my_registration: number | null;
  my_registration_status: RegistrationStatus | null;
  my_feedback: boolean;
  created_at: string;
}

export type RegistrationStatus = "registered" | "confirmed" | "cancelled" | "attended" | "no_show";

export interface EventRegistration {
  id: number;
  event: number;
  event_name: string;
  event_start_date: string;
  event_end_date: string;
  event_status: EventStatus;
  user_name: string;
  user_type: string;
  status: RegistrationStatus;
  registration_date: string;
  check_in_time?: string | null;
  dietary_requirements?: string | null;
  special_requests?: string | null;
  emergency_contact?: string | null;
  emergency_phone?: string | null;
  has_feedback: boolean;
}

export interface EventFeedback {
  id: number;
  event: number;
  event_name: string;
  event_start_date: string;
  user_name: string;
  user_type: string;
  overall_rating: number;
  organization_rating?: number | null;
  content_rating?: number | null;
  venue_rating?: number | null;
  comments?: string | null;
  suggestions?: string | null;
  would_attend_again: boolean;
  would_recommend: boolean;
  created_at: string;
}

export type TaskStatus = "pending" | "in_progress" | "completed" | "cancelled";

export interface EventTask {
  id: number;
  event: number;
  event_name: string;
  event_start_date: string;
  title: string;
  description?: string | null;
  priority: "low" | "medium" | "high" | "urgent";
  due_date?: string | null;
  due_time?: string | null;
  assigned_to: number;
  assigned_to_name: string;
  created_by_name: string;
  status: TaskStatus;
  completed_date?: string | null;
  completion_notes?: string | null;
  notes?: string | null;
}

export interface EventResource {
  id: number;
  event: number;
  name: string;
  description?: string | null;
  resource_type: string;
  quantity: number;
  unit: string;
  cost?: string | null;
  supplier?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  status: "required" | "ordered" | "received" | "returned";
  notes?: string | null;
}

export interface Assignee {
  id: number;
  full_name: string;
  role: string;
}

type Payload = Record<string, unknown>;

export const eventService = {
  getCategories: () => get<EventCategory>("/events/categories/"),
  createCategory: async (data: Payload) => (await api.post("/events/categories/", data)).data as EventCategory,
  updateCategory: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/events/categories/${id}/`, data)).data as EventCategory,
  deleteCategory: async (id: number) => {
    await api.delete(`/events/categories/${id}/`);
  },

  getEvents: () => get<SchoolEvent>("/events/events/"),
  createEvent: async (data: Payload) => (await api.post("/events/events/", data)).data as SchoolEvent,
  updateEvent: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/events/events/${id}/`, data)).data as SchoolEvent,
  deleteEvent: async (id: number) => {
    await api.delete(`/events/events/${id}/`);
  },
  publishEvent: async (id: number) => (await api.post(`/events/events/${id}/publish/`)).data as SchoolEvent,
  postponeEvent: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/events/events/${id}/postpone/`, { reason })).data as SchoolEvent,
  cancelEvent: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/events/events/${id}/cancel/`, { reason })).data as SchoolEvent,
  completeEvent: async (id: number) => (await api.post(`/events/events/${id}/complete/`)).data as SchoolEvent,

  getRegistrations: () => get<EventRegistration>("/events/registrations/"),
  getEventRegistrations: (event: number) => get<EventRegistration>(`/events/registrations/?event=${event}`),
  register: async (data: Payload) => (await api.post("/events/registrations/", data)).data as EventRegistration,
  withdraw: async (id: number) => (await api.post(`/events/registrations/${id}/withdraw/`)).data as EventRegistration,
  checkIn: async (id: number) => (await api.post(`/events/registrations/${id}/check_in/`)).data as EventRegistration,
  undoCheckIn: async (id: number) => (await api.post(`/events/registrations/${id}/undo_check_in/`)).data as EventRegistration,

  getFeedback: () => get<EventFeedback>("/events/feedback/"),
  createFeedback: async (data: Payload) => (await api.post("/events/feedback/", data)).data as EventFeedback,

  getTasks: () => get<EventTask>("/events/tasks/"),
  createTask: async (data: Payload) => (await api.post("/events/tasks/", data)).data as EventTask,
  updateTask: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/events/tasks/${id}/`, data)).data as EventTask,
  deleteTask: async (id: number) => {
    await api.delete(`/events/tasks/${id}/`);
  },
  getAssignees: async () => (await api.get("/events/tasks/assignees/")).data as Assignee[],

  getEventResources: (event: number) => get<EventResource>(`/events/resources/?event=${event}`),
  createResource: async (data: Payload) => (await api.post("/events/resources/", data)).data as EventResource,
  updateResource: async ({ id, data }: { id: number; data: Payload }) => (await api.patch(`/events/resources/${id}/`, data)).data as EventResource,
  deleteResource: async (id: number) => {
    await api.delete(`/events/resources/${id}/`);
  },
};
